import { SlaClock, SlaState, Ticket, TicketPriority, TicketStatus } from '@/services/tickets.service';

export const PRIORITY_META: Record<TicketPriority, { label: string; dot: string; pill: string }> = {
  urgent: { label: 'Urgent', dot: 'bg-danger', pill: 'bg-danger-soft text-danger' },
  high: { label: 'High', dot: 'bg-warning', pill: 'bg-warning-soft text-warning' },
  normal: { label: 'Normal', dot: 'bg-info', pill: 'bg-info-soft text-info' },
  low: { label: 'Low', dot: 'bg-text-muted', pill: 'bg-surface-alt text-text-muted' },
};

export const STATUS_META: Record<TicketStatus, { label: string; pill: string }> = {
  open: { label: 'Open', pill: 'bg-info-soft text-info' },
  pending: { label: 'Pending', pill: 'bg-warning-soft text-warning' },
  on_hold: { label: 'On hold', pill: 'bg-surface-alt text-text-muted' },
  resolved: { label: 'Resolved', pill: 'bg-success-soft text-success' },
  closed: { label: 'Closed', pill: 'bg-surface-alt text-text-muted' },
};

export const STATUS_ORDER: TicketStatus[] = ['open', 'pending', 'on_hold', 'resolved', 'closed'];
export const PRIORITY_ORDER: TicketPriority[] = ['urgent', 'high', 'normal', 'low'];

export function initialsFor(name?: string, email?: string): string {
  const src = (name ?? '').trim();
  if (src) {
    const parts = src.split(/\s+/).filter(Boolean);
    return parts.slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || '?';
  }
  return (email ?? '?').slice(0, 2).toUpperCase();
}

export function formatDuration(ms: number): string {
  const abs = Math.abs(ms);
  const minutes = Math.round(abs / 60_000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const restMinutes = minutes % 60;
  if (hours < 24) return restMinutes ? `${hours}h ${restMinutes}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  const restHours = hours % 24;
  return restHours ? `${days}d ${restHours}h` : `${days}d`;
}

/** "2h 10m left" / "Breached 35m ago" / "Met" / "Paused" — the live SLA chip text. */
export function slaCountdownLabel(clock: SlaClock, ticketStatus: TicketStatus, now: number = Date.now()): string {
  if (clock.state === 'met') return 'Met';
  if (ticketStatus === 'on_hold') return 'Paused';
  if (!clock.dueAt) return '—';
  const dueMs = new Date(clock.dueAt).getTime();
  const diff = dueMs - now;
  if (diff < 0) return `Breached ${formatDuration(diff)} ago`;
  return `${formatDuration(diff)} left`;
}

export const SLA_STATE_COLOR: Record<SlaState, string> = {
  met: 'text-success',
  on_track: 'text-text-secondary',
  at_risk: 'text-warning',
  breached: 'text-danger',
};

export const SLA_STATE_PILL: Record<SlaState, string> = {
  met: 'bg-success-soft text-success',
  on_track: 'bg-surface-alt text-text-secondary',
  at_risk: 'bg-warning-soft text-warning',
  breached: 'bg-danger-soft text-danger',
};

/** Worse of the two clocks — drives the row-level SLA chip (resolution is the headline clock once first response is met). */
export function primaryClock(t: Ticket): SlaClock {
  if (t.sla.firstResponse.state !== 'met' && t.sla.firstResponse.state !== 'on_track') return t.sla.firstResponse;
  return t.sla.resolution;
}

export function requesterLabel(t: Ticket): string {
  return t.requesterName || t.requesterEmail || t.requesterPhone || '—';
}
